/**
 * Employee selection component for shift forms
 * Fetches and displays employees in a searchable dropdown with avatars
 */

// TODO: Replace with server-side search, pagination, or virtualization for larger employee counts

import { useLingui } from '@lingui/react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { isSelectableEmployee, useAllEmployees } from '@/hooks/useEmployees';
import { useImageUrl } from '@/hooks/useImage';
import { isImageId, isDirectImageUrl } from '@/utils/imageUtils';
import { CircleDashed, Loader2 } from 'lucide-react';
import { OPEN_SHIFT_VALUE } from '@/schemas/shiftSchemas';
import {
  EnhancedSingleSelect,
  EnhancedSelectOption,
} from '@/components/ui/enhanced-singleselect';
import * as React from 'react';

export interface EmployeeSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  error?: string;
  id?: string;
  'aria-describedby'?: string;
  /**
   * Groups currently being planned. When non-empty the dropdown soft-filters to
   * employees belonging to one of these groups (plus all_groups employees). It is a
   * soft filter only: the "Show all employees" toggle widens the list to everyone, and
   * the currently-selected employee is always kept. This is NOT an authorization gate.
   */
  groupIds?: string[];
  /**
   * Offers "Open shift (nobody assigned)" as the first option (#2040). Its value is
   * OPEN_SHIFT_VALUE; the form maps it to open_shift.
   */
  allowOpenShift?: boolean;
}

export interface EmployeeData {
  id?: string;
  firstname?: string;
  lastname?: string;
  avatarUrl?: string;
  qualificationLevel?: string;
  languages?: string[];
  allGroups?: boolean;
  groupIds?: string[];
}

/**
 * Helper component to render employee avatar with image URL resolution
 */
export function EmployeeAvatar({ employee }: { employee: EmployeeData }) {
  const avatarUrlRaw = employee.avatarUrl;
  const avatarUrl = avatarUrlRaw?.trim() || undefined;

  // Check if avatarUrl is an image ID that needs resolution
  const needsImageResolution = isImageId(avatarUrl);

  // Use the useImageUrl hook to resolve image ID to URL if needed
  const resolvedImageUrl = useImageUrl(needsImageResolution ? avatarUrl : null);

  // Determine the final avatar URL to use
  let finalAvatarUrl: string | undefined;
  if (needsImageResolution) {
    finalAvatarUrl = resolvedImageUrl || undefined;
  } else if (isDirectImageUrl(avatarUrl)) {
    finalAvatarUrl = avatarUrl;
  } else {
    finalAvatarUrl = undefined;
  }

  const getInitials = (firstname?: string, lastname?: string) => {
    const firstInitial = firstname?.charAt(0)?.toUpperCase() || '';
    const lastInitial = lastname?.charAt(0)?.toUpperCase() || '';
    return `${firstInitial}${lastInitial}`;
  };

  const initials = getInitials(employee.firstname, employee.lastname);

  return (
    <Avatar className="h-6 w-6 flex-shrink-0">
      {finalAvatarUrl && (
        <AvatarImage
          src={finalAvatarUrl}
          alt={`${employee.firstname} ${employee.lastname}`}
        />
      )}
      <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

/**
 * A select component for choosing an employee.
 * Fetches employees using the useAllEmployees hook and displays them in a dropdown.
 */
export function EmployeeSelect({
  value,
  onChange,
  disabled,
  error,
  id,
  'aria-describedby': ariaDescribedBy,
  groupIds,
  allowOpenShift = false,
}: EmployeeSelectProps) {
  const { i18n } = useLingui();
  const { data: employeeList, isLoading, isError } = useAllEmployees();

  // "Show all employees" widens the soft filter to the full list.
  const [showAll, setShowAll] = React.useState(false);

  // The soft filter is active only when one or more groups are being planned and the
  // user has not asked to see everyone.
  const plannedGroupIds = React.useMemo(() => groupIds ?? [], [groupIds]);
  const filterActive = plannedGroupIds.length > 0 && !showAll;

  // Convert employees to EnhancedSelectOption format, applying the soft group filter.
  // The currently-selected employee is always kept so a prior selection never vanishes.
  const employeeOptions: EnhancedSelectOption[] = React.useMemo(() => {
    // Departed employees are not offered for a new shift (#1688); the current
    // value stays so an existing assignment never blanks.
    const employees = (employeeList || []).filter(
      (employee) => employee.id === value || isSelectableEmployee(employee)
    );
    const filtered = filterActive
      ? employees.filter(
          (employee) =>
            employee.id === value ||
            employee.allGroups === true ||
            (employee.groupIds?.some((g) => plannedGroupIds.includes(g)) ?? false)
        )
      : employees;
    return filtered.map((employee) => ({
      value: employee.id || '',
      label: `${employee.firstname} ${employee.lastname}`,
    }));
  }, [employeeList, filterActive, plannedGroupIds, value]);
  // Built per render, not memoised: a memo keyed on i18n misses a locale switch.
  const options: EnhancedSelectOption[] = allowOpenShift
    ? [
        {
          value: OPEN_SHIFT_VALUE,
          label: i18n._({ id: 'shifts.form.openShiftOption', message: 'Open shift (nobody assigned)' }),
        },
        ...employeeOptions,
      ]
    : employeeOptions;

  // Create a map for quick employee lookup
  const employeeMap = React.useMemo(() => {
    const employees = employeeList || [];
    const map = new Map<string, EmployeeData>();
    employees.forEach((employee) => {
      if (employee.id) {
        map.set(employee.id, {
          id: employee.id,
          firstname: employee.firstname,
          lastname: employee.lastname,
          avatarUrl: employee.avatarUrl,
          qualificationLevel: employee.qualificationLevel,
          languages: employee.languages
            ?.map((l) => l.language)
            .filter((l): l is string => !!l),
          allGroups: employee.allGroups,
          groupIds: employee.groupIds,
        });
      }
    });
    return map;
  }, [employeeList]);

  // Custom render function to display avatar with employee name + qualification/languages
  const renderEmployeeOption = React.useCallback(
    (option: EnhancedSelectOption) => {
      if (option.value === OPEN_SHIFT_VALUE) {
        return (
          <div className="flex items-center gap-2 min-w-0 font-medium" data-testid="open-shift-option">
            <CircleDashed className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
            <span className="truncate">{option.label}</span>
          </div>
        );
      }
      const employee = employeeMap.get(option.value);
      if (!employee) {
        return <span>{option.label}</span>;
      }

      return (
        <div className="flex items-center gap-2 min-w-0">
          <EmployeeAvatar employee={employee} />
          <span className="truncate">{option.label}</span>
          {employee.qualificationLevel === 'QUALIFIED' && (
            <Badge variant="secondary" className="text-xs px-1.5 py-0 shrink-0">
              {i18n._({ id: 'employees.qualification.qualified', message: 'Qualified' })}
            </Badge>
          )}
          {employee.languages?.map((lang) => (
            <Badge key={lang} variant="outline" className="text-xs px-1.5 py-0 uppercase shrink-0">
              {lang}
            </Badge>
          ))}
        </div>
      );
    },
    [employeeMap, i18n]
  );

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 h-10 px-3 border rounded-md bg-muted">
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        <span className="text-muted-foreground text-sm">
          {i18n._({ id: 'common.loading', message: 'Loading...' })}
        </span>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center h-10 px-3 border border-destructive rounded-md bg-destructive/10">
        <span className="text-destructive text-sm">
          {i18n._({ id: 'shifts.form.employeeLoadError', message: 'Failed to load employees' })}
        </span>
      </div>
    );
  }

  const showFilterToggle = plannedGroupIds.length > 0;

  return (
    <div className="space-y-1">
      <EnhancedSingleSelect
        id={id}
        value={value}
        onChange={onChange}
        options={options}
        disabled={disabled}
        error={!!error}
        aria-describedby={ariaDescribedBy}
        aria-invalid={!!error}
        placeholder={i18n._({
          id: 'shifts.form.selectEmployee',
          message: 'Select an employee',
        })}
        renderOption={renderEmployeeOption}
      />
      {showFilterToggle && (
        <Button
          type="button"
          variant="link"
          size="sm"
          className="h-auto p-0 text-xs text-muted-foreground hover:text-foreground"
          aria-pressed={showAll}
          onClick={() => setShowAll((prev) => !prev)}
        >
          {showAll
            ? i18n._({ id: 'shifts.employeeSelect.showGroupOnly', message: 'Show only this group' })
            : i18n._({ id: 'shifts.employeeSelect.showAll', message: 'Show all employees' })}
        </Button>
      )}
    </div>
  );
}
